import React, { useState, useEffect, useCallback } from 'react';
import { apiEndpoints, handleApiError } from '../services/api';
import { toast } from 'react-hot-toast';
import {
  Plus, Edit, Trash2, RefreshCw, Search, Building2, Phone, MapPin, X
} from 'lucide-react';

const SuppliersManagement = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({
    name: '',
    vendorName: '',
    firstName: '',
    address: '',
    phoneNumber: '',
    notes: ''
  });

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiEndpoints.suppliers.getAll();
      setSuppliers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      handleApiError(error, 'Erreur lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleAdd = () => {
    setEditingSupplier(null);
    setSupplierForm({
      name: '',
      vendorName: '',
      firstName: '',
      address: '',
      phoneNumber: '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm({
      name: supplier.name || '',
      vendorName: supplier.vendorName || '',
      firstName: supplier.firstName || '',
      address: supplier.address || '',
      phoneNumber: supplier.phoneNumber || '',
      notes: supplier.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur ?')) {
      return;
    }

    try {
      await apiEndpoints.suppliers.delete(id);
      toast.success('Fournisseur supprimé avec succès');
      fetchSuppliers();
    } catch (error) {
      handleApiError(error, 'Erreur lors de la suppression');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supplierForm.name.trim()) {
      toast.error('Le nom du fournisseur est requis');
      return;
    }

    try {
      const data = {
        name: supplierForm.name.trim(),
        vendorName: supplierForm.vendorName.trim() || null,
        firstName: supplierForm.firstName.trim() || null,
        address: supplierForm.address.trim() || null,
        phoneNumber: supplierForm.phoneNumber.trim() || null,
        notes: supplierForm.notes.trim() || null
      };

      if (editingSupplier) {
        await apiEndpoints.suppliers.update(editingSupplier.id, data);
        toast.success('Fournisseur modifié avec succès');
      } else {
        await apiEndpoints.suppliers.create(data);
        toast.success('Fournisseur ajouté avec succès');
      }
      setShowModal(false);
      fetchSuppliers();
    } catch (error) {
      handleApiError(error, 'Erreur lors de l\'enregistrement');
    }
  };

  const filteredSuppliers = suppliers.filter(supplier =>
    (supplier.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.vendorName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.phoneNumber || '').includes(searchTerm)
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-7 w-7 text-indigo-600" />
            Gestion des Fournisseurs
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Gérez vos fournisseurs (Ajouter, Modifier, Supprimer)
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 font-medium text-sm flex items-center gap-2 shadow-sm transition-colors"
        >
          <Plus className="h-4 w-4" />
          Ajouter un Fournisseur
        </button>
      </div>

      {/* Search & Action bar */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Rechercher par nom, nom commercial, téléphone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
          />
        </div>
        <button
          onClick={fetchSuppliers}
          className="px-3.5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm font-medium transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      {/* Suppliers Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-400 gap-3">
            <RefreshCw className="h-7 w-7 animate-spin text-indigo-600" />
            <span className="text-sm">Chargement des fournisseurs...</span>
          </div>
        ) : filteredSuppliers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Building2 className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="font-medium text-base">Aucun fournisseur trouvé</p>
            <p className="text-sm text-gray-400 mt-1">
              {searchTerm ? 'Essayez une autre recherche' : 'Cliquez sur "Ajouter un Fournisseur" pour commencer'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3.5 text-left font-semibold text-gray-600 uppercase tracking-wider text-xs">
                    Nom du Fournisseur
                  </th>
                  <th className="px-6 py-3.5 text-left font-semibold text-gray-600 uppercase tracking-wider text-xs">
                    Nom Commercial
                  </th>
                  <th className="px-6 py-3.5 text-left font-semibold text-gray-600 uppercase tracking-wider text-xs">
                    Prénom / Contact
                  </th>
                  <th className="px-6 py-3.5 text-left font-semibold text-gray-600 uppercase tracking-wider text-xs">
                    Téléphone
                  </th>
                  <th className="px-6 py-3.5 text-left font-semibold text-gray-600 uppercase tracking-wider text-xs">
                    Adresse
                  </th>
                  <th className="px-6 py-3.5 text-right font-semibold text-gray-600 uppercase tracking-wider text-xs">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredSuppliers.map((supplier) => (
                  <tr key={supplier.id} className="hover:bg-gray-50/80 transition-colors">
                    <td className="px-6 py-4 font-semibold text-gray-900 whitespace-nowrap">
                      {supplier.name}
                    </td>
                    <td className="px-6 py-4 text-gray-600 whitespace-nowrap">
                      {supplier.vendorName || '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-600 whitespace-nowrap">
                      {supplier.firstName || '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-600 whitespace-nowrap">
                      {supplier.phoneNumber ? (
                        <span className="flex items-center gap-1.5 text-gray-700">
                          <Phone className="h-3.5 w-3.5 text-gray-400" />
                          {supplier.phoneNumber}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-600 whitespace-nowrap max-w-[220px] truncate">
                      {supplier.address ? (
                        <span className="flex items-center gap-1.5 text-gray-700">
                          <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          {supplier.address}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(supplier)}
                          className="p-1.5 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-md transition-colors"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(supplier.id)}
                          className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">
                {editingSupplier ? 'Modifier le Fournisseur' : 'Ajouter un Fournisseur'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                  Nom du Fournisseur *
                </label>
                <input
                  type="text"
                  value={supplierForm.name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                  placeholder="Ex: SARL Equipement Equestre"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    Nom Commercial
                  </label>
                  <input
                    type="text"
                    value={supplierForm.vendorName}
                    onChange={(e) => setSupplierForm({ ...supplierForm, vendorName: e.target.value })}
                    placeholder="Ex: Etablissement Benali"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    Prénom / Contact
                  </label>
                  <input
                    type="text"
                    value={supplierForm.firstName}
                    onChange={(e) => setSupplierForm({ ...supplierForm, firstName: e.target.value })}
                    placeholder="Ex: Ahmed"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                  Téléphone
                </label>
                <input
                  type="text"
                  value={supplierForm.phoneNumber}
                  onChange={(e) => setSupplierForm({ ...supplierForm, phoneNumber: e.target.value })}
                  placeholder="Ex: 0550 12 34 56"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                  Adresse
                </label>
                <input
                  type="text"
                  value={supplierForm.address}
                  onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                  placeholder="Ex: Rue 1er Novembre, Alger"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                  Notes / Observations
                </label>
                <textarea
                  rows={2}
                  value={supplierForm.notes}
                  onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })}
                  placeholder="Remarques..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
                >
                  {editingSupplier ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuppliersManagement;
